import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngrx/store';
import {
  selectImpersonatedUser,
  selectIsImpersonating,
} from '@core/auth/auth.selectors';
import { authApiActions } from '@core/auth/auth.actions';
import { impersonatedDisplayName } from '@core/auth/impersonated-display-name';
import { SupportImpersonationService } from '@core/auth/support-impersonation/support-impersonation.service';

/**
 * Banner component that displays a warning strip when an admin is impersonating another user.
 * Where the M2b support-impersonation contract applies it also shows the mode (read-only by default)
 * and the deliberate edit-mode switch (#3335 S4–S6).
 * Follows the same design language as EnvironmentBannerComponent for visual consistency.
 * Renders in app.component.html alongside the environment banner.
 */
@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, DatePipe],
  template: `
    @if (isImpersonating()) {
      <div class="impersonation-banner" role="alert">
        <div class="impersonation-banner__content">
          <mat-icon class="impersonation-banner__icon"
            >supervisor_account</mat-icon
          >
          <span class="impersonation-banner__text">
            Impersonating
            <strong>{{ impersonatedDisplayName() }}</strong>
          </span>
          @switch (support.state().status) {
            @case ('read-only') {
              <span class="impersonation-banner__mode" data-testid="impersonation-mode"
                >Read-only</span
              >
            }
            @case ('edit') {
              <span
                class="impersonation-banner__mode impersonation-banner__mode--editing"
                data-testid="impersonation-mode"
                >Editing as this user until {{ editExpiresAt() | date: 'shortTime' }}</span
              >
            }
            @case ('refused') {
              <span class="impersonation-banner__mode" data-testid="impersonation-mode"
                >Not allowed</span
              >
            }
            @case ('unavailable') {
              <span class="impersonation-banner__mode" data-testid="impersonation-mode"
                >Read-only (could not check)</span
              >
            }
          }
        </div>
        <div class="impersonation-banner__actions">
          @if (support.state().status === 'read-only') {
            <button
              mat-button
              class="impersonation-banner__stop-btn"
              data-testid="impersonation-enter-edit"
              (click)="support.enterEditMode()"
              aria-label="Turn on edit mode: act as the impersonated user"
            >
              <mat-icon>edit</mat-icon>
              Edit as this user
            </button>
          }
          @if (support.state().status === 'edit') {
            <button
              mat-button
              class="impersonation-banner__stop-btn"
              data-testid="impersonation-leave-edit"
              (click)="support.leaveEditMode()"
              aria-label="Turn off edit mode"
            >
              <mat-icon>edit_off</mat-icon>
              Stop editing
            </button>
          }
          <button
            mat-button
            class="impersonation-banner__stop-btn"
            (click)="stopImpersonation()"
            aria-label="Stop impersonating user"
          >
            <mat-icon>person_off</mat-icon>
            Stop
          </button>
        </div>
      </div>
    }
  `,
  styleUrl: './impersonation-banner.component.scss',
})
export class ImpersonationBannerComponent {
  private readonly store = inject(Store);

  /** M2b: read-only unless the operator deliberately turns on edit mode (when the contract applies here). */
  protected readonly support = inject(SupportImpersonationService);

  protected readonly editExpiresAt = computed(() => {
    const state = this.support.state();
    return state.status === 'edit' ? state.expiresAt : null;
  });

  protected readonly isImpersonating = this.store.selectSignal(selectIsImpersonating);
  private readonly impersonatedUser = this.store.selectSignal(selectImpersonatedUser);

  protected readonly impersonatedDisplayName = computed(() =>
    impersonatedDisplayName(this.impersonatedUser())
  );

  /** Emitted when impersonation is stopped (for parent coordination) */
  readonly stopped = output<void>();

  stopImpersonation(): void {
    this.store.dispatch(authApiActions.stopUserImpersonation());
    this.stopped.emit();
  }
}
